import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, LitElement } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { magnetic } from '@schmancy/directives/magnetic'

export type FabVariant = 'surface' | 'primary' | 'secondary' | 'tertiary'
export type FabSize = 'small' | 'medium' | 'large'

/** Material 3 FAB. Never disabled per M3 guidance, so there is no `disabled` property. */
@customElement('schmancy-fab')
export class SchmancyFab extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				/* FAB (medium) — rounded-xl shape, 24dp icon, resting level 3 */
				display: inline-flex;
				flex-shrink: 0;
				position: relative;
				touch-action: manipulation;
				overflow: hidden;
				border-radius: var(--schmancy-radius-xl, 0.875rem); /* rounded-xl */
				--_icon: 24px;
				--_elevation: var(--schmancy-sys-elevation-3);
				--_elevation-hover: var(--schmancy-sys-elevation-4);
				box-shadow: var(--_elevation);
				transition:
					box-shadow 280ms cubic-bezier(0.34, 1.56, 0.64, 1),
					transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1);
			}
			/* small FAB — rounded-lg shape */
			:host([size='small']) {
				border-radius: var(--schmancy-radius-lg, 0.625rem); /* rounded-lg */
			}
			/* large FAB — rounded-3xl shape, 36dp icon */
			:host([size='large']) {
				border-radius: var(--schmancy-radius-3xl, 1.75rem); /* rounded-3xl */
				--_icon: 36px;
			}
			/* Extended FAB always uses the rounded-xl shape / 24dp icon */
			:host([extended]) {
				border-radius: var(--schmancy-radius-xl, 0.875rem); /* rounded-xl */
				--_icon: 24px;
			}
			/* M3 lowered register — resting level 1, hover level 2 */
			:host([lowered]) {
				--_elevation: var(--schmancy-sys-elevation-1);
				--_elevation-hover: var(--schmancy-sys-elevation-2);
			}
			:host(:hover) {
				box-shadow: var(--_elevation-hover);
			}
			:host(:active) {
				/* M3 pressed elevation == resting; schmancy spring press */
				box-shadow: var(--_elevation);
				transform: scale(0.96);
				transition-duration: 100ms;
			}
			::slotted(*) {
				font-size: var(--_icon);
				width: var(--_icon);
				height: var(--_icon);
			}
			:host([extended]) ::slotted(*) {
				margin-inline-end: 12px;
			}
			@media (prefers-reduced-motion: reduce) {
				:host {
					transition: none;
				}
				:host(:active) {
					transform: none;
				}
			}
			:host *,
			* {
				touch-action: manipulation;
			}
		`,
	]

	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		mode: 'open',
		delegatesFocus: true,
	}

	@query('[part="base"]', true)
	private nativeElement!: HTMLElement

	/** M3 maps each variant to a container + on-container colour role. */
	@property({ reflect: true, type: String })
	public variant: FabVariant = 'surface'

	/** Ignored while extended (extended is always 56dp tall). */
	@property({ reflect: true, type: String })
	public size: FabSize = 'medium'

	/** A non-empty value switches the FAB to its extended form (icon + text). */
	@property({ type: String })
	public label = ''

	/** Reflected mirror of "label is non-empty", driving `:host([extended])`
	 *  geometry. Synced from `label` — read it, don't set it. */
	@property({ type: Boolean, reflect: true })
	public extended = false

	@property({ type: Boolean, reflect: true })
	public lowered = false

	public override focus(options?: FocusOptions) {
		this.nativeElement.focus(options)
	}

	public override blur() {
		this.nativeElement.blur()
	}

	protected willUpdate(changed: Map<string, unknown>) {
		super.willUpdate(changed)
		if (changed.has('label')) this.extended = this.label.length > 0
	}

	render() {
		const classes = {
			'relative z-0 flex items-center justify-center overflow-hidden border-0 cursor-pointer outline-hidden focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 outline-secondary-default': true,
			// M3 container — fixed square per size, content-width when extended
			'size-10': !this.extended && this.size === 'small',
			'size-14': !this.extended && this.size === 'medium',
			'size-24': !this.extended && this.size === 'large',
			'h-14 ps-4 pe-5 gap-3 text-sm font-medium leading-5': this.extended,
			// M3 container + on-container colour roles per variant
			'bg-surface-containerHigh text-primary-default': this.variant === 'surface',
			'bg-primary-container text-primary-onContainer': this.variant === 'primary',
			'bg-secondary-container text-secondary-onContainer': this.variant === 'secondary',
			'bg-tertiary-container text-tertiary-onContainer': this.variant === 'tertiary',
		}

		const stateLayerClasses = {
			'absolute inset-0 z-0 opacity-0 transition-opacity duration-150 hover:opacity-[var(--schmancy-sys-state-opacity-hover,0.08)] focus-visible:opacity-[var(--schmancy-sys-state-opacity-focus,0.12)] active:opacity-[var(--schmancy-sys-state-opacity-pressed,0.12)]': true,
			'bg-primary-default': this.variant === 'surface',
			'bg-primary-onContainer': this.variant === 'primary',
			'bg-secondary-onContainer': this.variant === 'secondary',
			'bg-tertiary-onContainer': this.variant === 'tertiary',
		}

		return html`
			<button
				${magnetic({ strength: 3, radius: 60 })}
				part="base"
				type="button"
				aria-label=${ifDefined(this.ariaLabel || undefined)}
				class="${this.classMap(classes)}"
			>
				<div class="${this.classMap(stateLayerClasses)}"></div>
				<slot aria-hidden=${ifDefined(this.ariaLabel || this.label ? 'true' : undefined)}></slot>
				${when(this.extended, () => html`<span class="relative z-0">${this.label}</span>`)}
			</button>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-fab': SchmancyFab
	}
}
